import AltRouteIcon from "@mui/icons-material/AltRoute";
import React from "react";
import { Stack, Button, ButtonGroup, IconButton, Tooltip } from "@mui/material";
import { AutoParkProps } from "./types";
import { AutoParkButton } from "./components/AutoParkButton";
import { ParkNextButton } from "./components/ParkNextButton";
import { DecisionLogButton } from "./components/DecisionLogButton";
import { DecisionLogDialog } from "./components/DecisionLogDialog";
import { AutoParkDialog } from "./AutoParkDialog";
import { useAutoPark } from "./hooks/useAutoPark";
import { aircraftToSizeCategory } from "../TemporalTest/temporalRampIntegration";
import { AddLastMinuteTripDialog } from "../LastMinuteInsertion";
import { useActiveFBOs } from "../../../containers/ActiveFBOContainer";
import { getSpotForPositionLegacy } from "../../../utils/spots";

export const AutoPark: React.FC<AutoParkProps> = ({
  ramp,
  stack,
  setStack,
  isScheduledView,
  placementService,
  currentTime,
  postgrest,
  fbo,
  onScheduledUpdate,
  isDrawerOpen = true,
}) => {
  const {
    dialogOpen,
    parkingConfig,
    handleOpenDialog,
    handleCloseDialog,
    handleAutoPark,
    handleIncrementalPark,
    handleParkNext,
    logDialogOpen,
    handleOpenLogDialog,
    handleCloseLogDialog,
    lastResult,
    timeRange,
    handleTimeRangeChange,
    tripCount,
    placements,
    groupedTrips,
    selectedTripIds,
    handleToggleTripSelection,
    handleSelectAllTrips,
    handleDeselectAllTrips,
    handleApplyResults,
    isApplying,
    applyProgress,
    handleScheduleChanged,
  } = useAutoPark(ramp, stack, setStack, isScheduledView, placementService, currentTime, postgrest, fbo, onScheduledUpdate);

  const { activeFBO } = useActiveFBOs();

  // Check both FBO-wide setting and per-ramp setting
  const isFBOAutoParkingEnabled = activeFBO?.settings?.enableAutoParking ?? false;
  const isRampAutoParkingEnabled = ramp.autoparking_enabled ?? false;
  const isEnabled = isFBOAutoParkingEnabled && isRampAutoParkingEnabled;

  // If FBO-wide setting is disabled, don't show any auto parking UI
  if (!isFBOAutoParkingEnabled) {
    return null;
  }

  // State for last-minute insertion dialog (Phase 7)
  const [lastMinuteDialogOpen, setLastMinuteDialogOpen] = React.useState(false);

  // Calculate if we have aircraft without spots (for button enabled state)
  const hasUnplacedAircraft = React.useMemo(() => {
    // Helper: Check if aircraft has a spot assigned
    const hasSpot = (tenant: typeof stack.tenants[0]): boolean => {
      if (tenant.position.x === null || tenant.position.y === null) {
        return false; // Unplaced = no spot
      }

      // Build legacy spots from ramp shapes
      const spotShapes = ramp.shapes?.filter((s) => s.type === "Spot") || [];
      const legacySpots = spotShapes.map((shape) => ({
        points: shape.points,
        referencePoints: shape.reference_points || [],
      }));

      // Transform position from top-left origin to bottom-left origin
      // (getSpotForPositionLegacy expects bottom-left origin coordinates)
      const transformedPosition = {
        x: tenant.position.x,
        y: ramp.depth - tenant.position.y, // Y-flip transformation
        angle: tenant.position.angle,
      };

      // Use getSpotForPositionLegacy to check if aircraft is in a spot
      const result = getSpotForPositionLegacy(
        tenant,
        transformedPosition, // Use transformed position
        legacySpots,
        {}
      );

      return result !== undefined;
    };

    // Check if selected aircraft doesn't have a spot
    const selectedAircraft = stack.tenants.find((t) => t.selected);
    if (selectedAircraft && !hasSpot(selectedAircraft)) {
      return true;
    }

    // Check if any aircraft doesn't have a spot
    return stack.tenants.some((t) => !hasSpot(t));
  }, [stack.tenants, ramp.shapes, ramp.depth]);

  // Build parking requests for decision log viewer
  // Use requests from temporalData if available (temporal parking)
  // Otherwise reconstruct from stack/placements (snapshot parking)
  const parkingRequests = React.useMemo(() => {
    if (!lastResult) return [];

    // If we have temporal data, use the exact requests from the algorithm
    if ("temporalData" in lastResult && lastResult.temporalData?.requests) {
      return lastResult.temporalData.requests;
    }

    // Fallback for snapshot parking: reconstruct from stack
    return stack.tenants.map((tenant) => ({
      id: tenant.id,
      tailNumber: tenant.tail_number,
      sizeCategory: aircraftToSizeCategory(tenant.aircraft),
      arrivalBucket: 0,
      departureBucket: 1,
      aircraft: tenant.aircraft,
    }));
  }, [lastResult, stack.tenants]);

  const compact = !isDrawerOpen;

  return (
    <>
      <Stack
        direction="column"
        spacing={1}
        alignItems={compact ? "center" : "stretch"}
        sx={{ mb: 1 }}
      >
        {/* Primary Actions: AutoPark + Park Next */}
        {compact ? (
          // Collapsed: Vertical stack of icon buttons
          <>
            <AutoParkButton
              onClick={handleOpenDialog}
              disabled={!isEnabled}
              disabledMessage="AutoParking is not enabled for this ramp. Enable it in the ramp settings."
              compact={compact}
              inGroup={false}
            />
            <ParkNextButton
              onClick={handleParkNext}
              disabled={!isEnabled || !hasUnplacedAircraft}
              disabledMessage={
                !isEnabled
                  ? "AutoParking is not enabled for this ramp."
                  : "No unplaced aircraft available."
              }
              compact={compact}
              inGroup={false}
            />
            <Tooltip title="Check Fit">
              <span>
                <IconButton
                  onClick={() => setLastMinuteDialogOpen(true)}
                  disabled={!isScheduledView}
                  color="primary"
                  size="small"
                >
                  <AltRouteIcon />
                </IconButton>
              </span>
            </Tooltip>
            <DecisionLogButton
              onClick={handleOpenLogDialog}
              disabled={!lastResult || lastResult.decisionLog.length === 0}
            />
          </>
        ) : (
          // Expanded: All buttons on single row
          <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
            <AutoParkButton
              onClick={handleOpenDialog}
              disabled={!isEnabled}
              disabledMessage="AutoParking is not enabled for this ramp. Enable it in the ramp settings."
              compact={false}
              inGroup={false}
            />
            <ParkNextButton
              onClick={handleParkNext}
              disabled={!isEnabled || !hasUnplacedAircraft}
              disabledMessage={
                !isEnabled
                  ? "AutoParking is not enabled for this ramp."
                  : "No unplaced aircraft available."
              }
              compact={false}
              inGroup={false}
            />
            <Button
              onClick={() => setLastMinuteDialogOpen(true)}
              disabled={!isScheduledView}
              variant="outlined"
              color="primary"
              startIcon={<AltRouteIcon />}
              size="small"
            >
              Check Fit
            </Button>
            <DecisionLogButton
              onClick={handleOpenLogDialog}
              disabled={!lastResult || lastResult.decisionLog.length === 0}
            />
          </Stack>
        )}
      </Stack>
      <AutoParkDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        config={parkingConfig.config}
        onConfigChange={(updates) => {
          if (updates.bufferMinutes !== undefined) parkingConfig.setBufferMinutes(updates.bufferMinutes);
          if (updates.plannedMoveCap !== undefined) parkingConfig.setPlannedMoveCap(updates.plannedMoveCap);
          if (updates.allowTrapping !== undefined) parkingConfig.setAllowTrapping(updates.allowTrapping);
          if (updates.egressWeight !== undefined) parkingConfig.setEgressWeight(updates.egressWeight);
          if (updates.wasteWeight !== undefined) parkingConfig.setWasteWeight(updates.wasteWeight);
          if (updates.structuralWeight !== undefined) parkingConfig.setStructuralWeight(updates.structuralWeight);
          if (updates.zonePriorityWeight !== undefined) parkingConfig.setZonePriorityWeight(updates.zonePriorityWeight);
          if (updates.min_dwell_weight !== undefined) parkingConfig.setMinDwellWeight(updates.min_dwell_weight);
          if (updates.sortingStrategy !== undefined) parkingConfig.setSortingStrategy(updates.sortingStrategy);
          if (updates.arrivalStagingDwellMinutes !== undefined) parkingConfig.setArrivalStagingDwellMinutes(updates.arrivalStagingDwellMinutes);
          if (updates.departureStagingDwellMinutes !== undefined) parkingConfig.setDepartureStagingDwellMinutes(updates.departureStagingDwellMinutes);
          if (updates.quickTurnThresholdMinutes !== undefined) parkingConfig.setQuickTurnThresholdMinutes(updates.quickTurnThresholdMinutes);
        }}
        onRunAutoPark={handleAutoPark}
        isScheduledView={isScheduledView}
        timeRange={timeRange}
        onTimeRangeChange={handleTimeRangeChange}
        tripCount={tripCount}
        placements={placements}
        groupedTrips={groupedTrips}
        selectedTripIds={selectedTripIds}
        onToggleTripSelection={handleToggleTripSelection}
      />
      {lastResult && logDialogOpen && (
        <DecisionLogDialog
          open={logDialogOpen}
          onClose={handleCloseLogDialog}
          decisionLog={lastResult.decisionLog}
          requests={parkingRequests}
          rampShapes={ramp.shapes}
          spots={lastResult.temporalData?.spots}
          placement={lastResult.placement}
          zones={lastResult.temporalData?.zones}
          horizonHours={lastResult.temporalData?.horizonHours}
          startTime={lastResult.temporalData?.startTime}
          partialPlacements={lastResult.temporalData?.partialPlacements}
          statistics={lastResult.statistics}
          onApply={isScheduledView ? handleApplyResults : undefined}
          isApplying={isApplying}
          applyProgress={applyProgress}
          showDetailedView={fbo?.settings.displayDetailedDecisionLog ?? false}
          onScheduleChanged={handleScheduleChanged}
        />
      )}
      <AddLastMinuteTripDialog
        open={lastMinuteDialogOpen}
        onClose={() => setLastMinuteDialogOpen(false)}
        ramp={ramp}
        stack={stack}
        placements={placements}
        currentTime={currentTime}
      />
    </>
  );
};